import { useEffect, useRef, useState, type RefObject } from 'react';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { GroupHeader } from '@hivebrite/design-system/GroupHeader';
import { zIndices } from '@hivebrite/design-system/Theme';

import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import MenuScrollable from 'hb-react/shared/apps/Topics/components/TopicHeaderV3/MenuScrollable';
import { useSearchTopic } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/hooks/useSearchTopic';
import { useVerticalScroll } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/hooks/useVerticalScroll';
import type { TopicHeaderProps } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/types';
import { getGroupHeaderButtons } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/utils/getGroupHeaderButtons';
import { ImmersiveMenu } from 'hb-react/shared/hooks/useImmersiveMenu';

interface UIContextType {
  mainNetworkHeaderRef: RefObject<HTMLDivElement>;
}

export const TopicHeaderMobile = ({
  currentNetwork,
  currentUser,
  following,
  members,
  socials,
  topic,
  totalMembers,
  unfollowTopic,
  updateSettings,
}: TopicHeaderProps) => {
  const [networkHeaderHeight, setNetworkHeaderHeight] = useState(0);
  const [isCollapsedHeader, setIsCollapsedHeader] = useState(false);

  const mainGroupHeaderRef = useRef<HTMLDivElement>(null);

  const isPO = !currentUser;

  const { mainNetworkHeaderRef } = useUIContext() as unknown as UIContextType;
  const { handleSearch } = useSearchTopic({ topic });
  const { JoinButton, threeDotsButtonData } = getGroupHeaderButtons({
    following,
    isPO,
    unfollowTopic,
    updateSettings,
    topic,
  });
  const { isScrollingUp, currentScrollY } = useVerticalScroll();

  const collapsedHeaderSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(-100%)', display: 'none' },
    to: {
      opacity: isCollapsedHeader ? 1 : 0,
      transform: isCollapsedHeader ? 'translateY(0)' : 'translateY(-100%)',
      display: isCollapsedHeader ? 'block' : 'none',
    },
  });

  const calculateMainGroupHeaderHeight = () =>
    mainGroupHeaderRef.current ? mainGroupHeaderRef.current.offsetHeight : 0;

  const handleDisplayCollapsedHeader = () => {
    const mainGroupHeaderAndNetworkHeaderHeight =
      calculateMainGroupHeaderHeight();
    const isMainGroupHeaderVisible =
      currentScrollY < mainGroupHeaderAndNetworkHeaderHeight;

    if (isScrollingUp && !isMainGroupHeaderVisible) {
      setIsCollapsedHeader(true);
    } else {
      setIsCollapsedHeader(false);
    }
  };

  useEffect(() => {
    if (mainNetworkHeaderRef?.current) {
      setNetworkHeaderHeight(mainNetworkHeaderRef.current.offsetHeight);
    } else if (isPO) {
      // If we are in PO. We have to get the height of the header from the DOM
      // Header in PO is currently in haml.
      const header = document.querySelector('header.publicoffice-header');

      setNetworkHeaderHeight((header as HTMLElement)?.offsetHeight || 0);
    }
  }, [mainNetworkHeaderRef, isPO]);

  useEffect(() => {
    handleDisplayCollapsedHeader();
  }, [currentScrollY, isScrollingUp]);

  return (
    <ImmersiveMenu location={window.location} network={currentNetwork}>
      {({ isImmersiveMenu }) => (
        <>
          <Box
            // Large tablet are considered as mobile but as 'L'
            padding={{ l: '16px 32px' }}
            ref={mainGroupHeaderRef}
            data-testid="main-group-header"
            flexDirection="column"
            width="100%"
          >
            <GroupHeader
              MenuComponent={
                <Box
                  mt={{ _: 3, m: 4 }}
                  // Large tablet are considered as mobile but as 'L'
                  position="relative"
                >
                  <MenuScrollable topic={topic} />
                </Box>
              }
              JoinButtonComponent={JoinButton}
              collapsed={false}
              coverPictureUrl={topic.cover_picture_small_url}
              groupName={topic.name}
              logoUrl={topic.logo_url}
              members={members}
              onSearch={isPO ? undefined : handleSearch}
              socials={socials}
              totalMembers={totalMembers}
              threeDotsButtonData={threeDotsButtonData}
            />
          </Box>

          <AnimatedCollapsedContainer
            $headerHeight={isImmersiveMenu ? 0 : networkHeaderHeight}
            aria-hidden
            data-testid="collapsed-group-header"
            // Large tablet are considered as mobile but as 'L'
            padding={{ l: '0 32px' }}
            style={collapsedHeaderSpring}
            tabIndex={-1}
          >
            <GroupHeader
              MenuComponent={
                <Box
                  mt={{ m: 4, xs: 2 }}
                  // Large tablet are considered as mobile but as 'L'
                  mx={{ _: 0, l: 0 }}
                  position="relative"
                >
                  <MenuScrollable topic={topic} />
                </Box>
              }
              collapsed
              coverPictureUrl={topic.cover_picture_small_url}
              groupName={topic.name}
              logoUrl={topic.logo_url}
              socials={[]}
              totalMembers={totalMembers}
            />
          </AnimatedCollapsedContainer>
        </>
      )}
    </ImmersiveMenu>
  );
};

const AnimatedBox = animated(Box);

const AnimatedCollapsedContainer = styled(AnimatedBox as any)<{
  $headerHeight: number;
}>`
  flex-direction: column;
  position: fixed;
  top: ${({ $headerHeight }) => $headerHeight}px;
  left: 0;
  right: 0;
  z-index: ${zIndices.positioned};
`;
